import { Link } from "react-router-dom";
import { Sprout } from "lucide-react";

const Section = ({ eyebrow, title, children }) => (
  <section
    className="mb-14"
    data-testid={`terms-section-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="w-2 h-2 rounded-full bg-orange-500" />
      <span className="eyebrow text-orange-500">{eyebrow}</span>
    </div>
    <h2 className="font-serif-display text-3xl md:text-4xl text-stone-100 mb-6 leading-tight">
      {title}
    </h2>
    <div className="text-stone-300 leading-relaxed text-lg space-y-4">
      {children}
    </div>
  </section>
);

const Terms = () => (
  <div className="min-h-screen bg-stone-950 pt-32 pb-24">
    <div className="max-w-3xl mx-auto px-6 md:px-10" data-testid="terms-page">
      <div className="flex items-baseline gap-6 mb-3">
        <span className="font-mono-accent text-orange-500 text-sm">/terms</span>
        <span className="h-px w-24 bg-stone-800" />
        <span className="eyebrow text-stone-400">The Fine Print</span>
      </div>
      <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-stone-100 leading-[0.95] mb-4">
        Terms, plainly said.
      </h1>
      <p className="text-stone-400 text-lg max-w-2xl mb-16">
        The rules of the kitchen. Short, sensible, no ambush clauses.
      </p>

      <Section eyebrow="The deal" title="What Cinder & Sorrel is">
        <p>
          Cinder &amp; Sorrel is a cooking journal — a growing library of curated
          recipes, an AI recipe generator, and a handful of tools (favourites,
          cookbooks, cook photos) for the home cook. It is not a professional
          culinary school and not a medical or nutritional service.
        </p>
        <p>
          By using the site you agree to these terms and to our{" "}
          <Link
            to="/privacy"
            className="text-orange-400 hover:text-orange-300"
          >
            privacy policy
          </Link>
          . If you don't, please don't use the site.
        </p>
      </Section>

      <Section eyebrow="Accounts" title="You're responsible for your own kitchen">
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>You must be at least 16 to open an account.</li>
          <li>Keep your password to yourself. If you think it's been leaked,
              change it and email us.</li>
          <li>One person, one account. Please don't share log-ins to dodge
              the generation limits.</li>
          <li>We may suspend an account that abuses the service (spam, scraping,
              obscene cook-photo uploads, gaming the free-tier limits).</li>
        </ul>
      </Section>

      <Section eyebrow="Recipes & content" title="Who owns what">
        <p>
          Curated recipes, AI-generated recipes, plating photos, editorial copy,
          the brand and typography — all © Cinder &amp; Sorrel unless credited
          otherwise. You're free to <span className="text-stone-100">cook the
          recipes for personal or private household use</span>, print them, share
          the public share-links, and post a photo of your finished plate on your
          own socials.
        </p>
        <p>
          You may <span className="text-stone-100">not</span>: reproduce recipes
          in commercial cookbooks, feed them into another AI service for
          training, resell them, or strip the branding and republish them.
        </p>
        <p>
          <span className="text-stone-100">Cook photos you upload</span> stay
          yours. By uploading you grant us a non-exclusive licence to display the
          photo on the relevant recipe page and in our social feeds. You can
          delete a photo at any time and the licence ends.
        </p>
      </Section>

      <Section eyebrow="AI generation" title="A tool, not a chef">
        <p>
          The AI generator writes recipes using Claude (Anthropic) and plates
          them with Gemini (Google). It's remarkably good, but it is a language
          model — it can occasionally get quantities, timings, or geography
          wrong. Use judgement. If a step reads odd, trust your nose over the
          model. If you spot a factual howler, tell us and we'll fix it.
        </p>
        <p>
          Cinder &amp; Sorrel does not warrant that any generated recipe is fit
          for a specific dietary requirement. If you have an allergy, an
          intolerance, or a medical diet, verify every ingredient yourself.
        </p>
      </Section>

      <Section eyebrow="Subscriptions" title="Paying for AI generations">
        <p>
          Paid plans are billed monthly (£7) or annually (£77) through Stripe.
          You get up to 5 AI generations per day; free-tier accounts get zero
          generations but full read access to the curated library.
        </p>
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>Subscriptions renew automatically until you cancel. Cancel from{" "}
              <Link to="/account" className="text-orange-400 hover:text-orange-300">/account</Link> at any time.</li>
          <li>Cancellation stops the next renewal. Access continues until the end
              of the paid period.</li>
          <li>UK &amp; EU consumer law gives you a 14-day cooling-off period.
              If you cancel within 14 days and haven't used any AI generations,
              email us for a refund.</li>
          <li>Prices may change; we'll email at least 30 days ahead of any
              change to renewals.</li>
        </ul>
      </Section>

      <Section eyebrow="Fair use" title="Don't crash the kitchen">
        <p>
          Automated scraping, load-testing, or trying to break the site is not
          permitted. Rate limits apply to the AI generator regardless of plan.
        </p>
      </Section>

      <Section eyebrow="Food safety" title="Cook it properly">
        <p>
          Follow safe food-handling practice. Cook meat, poultry and eggs to
          appropriate temperatures. Foraging recipes (wild garlic, sea
          buckthorn, penny buns, etc.) assume you have positively identified the
          species — if in doubt, don't eat it. Cinder &amp; Sorrel takes no
          responsibility for foraging misidentification.
        </p>
      </Section>

      <Section eyebrow="Liability" title="The usual reasonable limits">
        <p>
          To the extent permitted by law, Cinder &amp; Sorrel is not liable for
          indirect or consequential loss arising from your use of the site or
          from cooking a recipe. Nothing in these terms excludes liability for
          fraud, death or personal injury caused by our negligence, or any
          liability that cannot legally be excluded under UK consumer law.
        </p>
      </Section>

      <Section eyebrow="Changes" title="If we update these terms">
        <p>
          We'll update this page and, for anything material, email account
          holders 30 days in advance. Continued use of the site after the change
          means you accept the new terms.
        </p>
      </Section>

      <Section eyebrow="Contact & law" title="Where to write, whose courts">
        <p>
          Reach the kitchen via the contact address in the footer. These terms
          are governed by the laws of Scotland; disputes fall under the
          non-exclusive jurisdiction of the Scottish courts.
        </p>
      </Section>

      <div className="mt-20 pt-8 border-t border-stone-800 flex items-center gap-3 text-stone-500 text-sm">
        <Sprout className="w-4 h-4 text-lime-600" />
        <span>Last updated: February 2026</span>
      </div>
    </div>
  </div>
);

export default Terms;
